import { Outlet, Link, useLocation } from "react-router-dom";
import { Truck, Phone, Mail, MapPin, Menu, X } from "lucide-react";
import { useState } from "react";
import WhatsAppButton from "./WhatsAppButton";

const Layout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Request Service", path: "/request-service" },
    { name: "Contact", path: "/contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation */}
      <nav className="bg-slate-900 text-white sticky top-0 z-40" data-testid="main-navbar">
        <div className="section-container">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
              <div className="bg-orange-600 p-2 rounded-sm">
                <Truck className="h-6 w-6" />
              </div>
              <div>
                <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  KDA TRUCKING
                </span>
                <span className="text-orange-500 ml-1 text-sm">LLC</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                  className={`text-sm font-semibold uppercase tracking-wider transition-colors duration-200 ${
                    isActive(link.path)
                      ? "text-orange-500"
                      : "text-slate-300 hover:text-white"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/request-service"
                data-testid="nav-cta-button"
                className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-sm font-bold text-sm uppercase tracking-wider transition-all duration-200 hover:-translate-y-0.5"
              >
                Get a Quote
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-6 animate-fade-in" data-testid="mobile-menu">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-3 text-sm font-semibold uppercase tracking-wider ${
                    isActive(link.path) ? "text-orange-500" : "text-slate-300"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/request-service"
                onClick={() => setMobileMenuOpen(false)}
                className="block mt-4 bg-orange-600 text-white px-6 py-3 rounded-sm font-bold text-sm uppercase tracking-wider text-center"
              >
                Get a Quote
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-grow">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16" data-testid="main-footer">
        <div className="section-container">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {/* Company Info */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-orange-600 p-2 rounded-sm">
                  <Truck className="h-6 w-6" />
                </div>
                <div>
                  <span className="font-bold text-2xl tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    KDA TRUCKING
                  </span>
                  <span className="text-orange-500 ml-1">LLC</span>
                </div>
              </div>
              <p className="text-slate-400 mb-6 max-w-md">
                Reliable and efficient dry van freight transportation service. 
                Connecting your business to destinations across the United States.
              </p>
              <div className="flex gap-4">
                <a 
                  href="tel:+16179132934" 
                  className="bg-slate-800 hover:bg-slate-700 p-3 rounded-sm transition-colors"
                  data-testid="footer-phone-link"
                >
                  <Phone className="h-5 w-5" />
                </a>
                <a 
                  href="mailto:rafael@kdatruckingllc.com" 
                  className="bg-slate-800 hover:bg-slate-700 p-3 rounded-sm transition-colors"
                  data-testid="footer-email-link"
                >
                  <Mail className="h-5 w-5" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-bold uppercase tracking-wider mb-6" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Quick Links
              </h4>
              <ul className="space-y-3">
                {navLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-slate-400 hover:text-orange-500 transition-colors text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Service Areas */}
            <div>
              <h4 className="font-bold uppercase tracking-wider mb-6" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Service Areas
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/trucking/massachusetts" className="text-slate-400 hover:text-orange-500 transition-colors text-sm">
                    Massachusetts Trucking
                  </Link>
                </li>
                <li>
                  <Link to="/trucking/new-hampshire" className="text-slate-400 hover:text-orange-500 transition-colors text-sm">
                    New Hampshire Trucking
                  </Link>
                </li>
                <li>
                  <Link to="/trucking/maine" className="text-slate-400 hover:text-orange-500 transition-colors text-sm">
                    Maine Trucking
                  </Link>
                </li>
                <li>
                  <Link to="/trucking/rhode-island" className="text-slate-400 hover:text-orange-500 transition-colors text-sm">
                    Rhode Island Trucking
                  </Link>
                </li>
                <li>
                  <Link to="/admin" className="text-slate-400 hover:text-orange-500 transition-colors text-sm">
                    Admin
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-bold uppercase tracking-wider mb-6" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Contact
              </h4>
              <ul className="space-y-4 text-sm text-slate-400">
                <li className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <span>Revere, MA 02151</span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-orange-500 flex-shrink-0" />
                  <span>(617) 913-2934</span>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-orange-500 flex-shrink-0" />
                  <span>rafael@kdatruckingllc.com</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-slate-800 mt-12 pt-8 text-center text-slate-500 text-sm">
            <p>&copy; {new Date().getFullYear()} KDA Trucking LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Button */}
      <WhatsAppButton />
    </div>
  );
};

export default Layout;
